import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
  Zoom,
  Paper,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Close,
  ZoomIn,
  ZoomOut,
  RotateLeft,
  RotateRight,
  Download,
  Fullscreen,
  FullscreenExit,
  Verified,
  Warning,
} from '@mui/icons-material';
import { DocumentService, SecureDocumentUrl } from '@/utils/document-service';

interface DocumentViewerModalProps {
  open: boolean;
  onClose: () => void;
  documentId: string | null;
  documentType?: 'id_front' | 'id_back';
  guestName?: string;
  verified?: boolean;
}

export const DocumentViewerModal: React.FC<DocumentViewerModalProps> = ({
  open,
  onClose,
  documentId,
  documentType,
  guestName,
  verified = false,
}) => {
  const [documentUrl, setDocumentUrl] = useState<SecureDocumentUrl | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));


  const loadDocument = useCallback(async () => {
    if (!documentId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const secureUrl = await DocumentService.getSecureDocumentUrl(documentId);
      setDocumentUrl(secureUrl);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load document');
    } finally {
      setLoading(false);
    }
  }, [documentId]);
  useEffect(() => {
    if (open && documentId) {
      loadDocument();
    } else {
      // Reset state when modal closes
      setDocumentUrl(null);
      setError(null);
      setZoom(1);
      setRotation(0);
      setFullscreen(false);
    }
  }, [open, documentId, loadDocument]);

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 0.25, 0.25));
  };

  const handleRotateLeft = () => {
    setRotation(prev => prev - 90);
  };

  const handleRotateRight = () => {
    setRotation(prev => prev + 90);
  };

  const handleDownload = () => {
    if (documentUrl?.viewUrl) {
      const link = document.createElement('a');
      link.href = documentUrl.viewUrl;
      link.download = `document_${documentId}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const toggleFullscreen = () => {
    setFullscreen(prev => !prev);
  };

  const getDocumentTypeLabel = () => {
    switch (documentType) {
      case 'id_front':
        return 'ID Document - Front';
      case 'id_back':
        return 'ID Document - Back';
      default:
        return 'Document';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={fullscreen ? false : 'lg'}
      fullWidth
      fullScreen={fullscreen || isMobile}
      PaperProps={{
        sx: {
          height: fullscreen ? '100vh' : '90vh',
          bgcolor: 'grey.900',
        }
      }}
    >
      <DialogTitle sx={{ bgcolor: 'grey.800', color: 'white', py: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6">
              {getDocumentTypeLabel()}
            </Typography>
            {guestName && (
              <Typography variant="body2" color="grey.300">
                Guest: {guestName}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label={verified ? 'Verified' : 'Unverified'}
              color={verified ? 'success' : 'warning'}
              size="small"
              icon={verified ? <Verified fontSize="small" /> : <Warning fontSize="small" />}
            />
            <IconButton onClick={onClose} size="small" sx={{ color: 'white' }}>
              <Close />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ bgcolor: 'grey.900', p: 0, position: 'relative' }}>
        {loading && (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '100%',
            color: 'white'
          }}>
            <CircularProgress color="inherit" />
          </Box>
        )}

        {error && (
          <Box sx={{ p: 3 }}>
            <Alert severity="error">
              {error}
            </Alert>
          </Box>
        )}

        {documentUrl && !loading && !error && (
          <Box sx={{ 
            height: '100%', 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            overflow: 'auto',
            position: 'relative'
          }}>
            {/* Watermark */}
            <Box sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%) rotate(-45deg)',
              fontSize: '4rem',
              color: 'rgba(255, 255, 255, 0.1)',
              fontWeight: 'bold',
              pointerEvents: 'none',
              zIndex: 1,
              userSelect: 'none',
            }}>
              CONFIDENTIAL
            </Box>

            <img
              src={documentUrl.viewUrl}
              alt="Document"
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                transform: `scale(${zoom}) rotate(${rotation}deg)`,
                transition: 'transform 0.2s ease-in-out',
                objectFit: 'contain',
              }}
              onError={() => setError('Failed to load document image')}
            />
          </Box>
        )}

        {/* Document expiration warning */}
        {documentUrl && (
          <Box sx={{ 
            position: 'absolute', 
            bottom: 16, 
            left: 16, 
            bgcolor: 'rgba(0, 0, 0, 0.7)', 
            color: 'white', 
            p: 1, 
            borderRadius: 1 
          }}>
            <Typography variant="caption">
              Secure view expires: {new Date(documentUrl.expiresAt).toLocaleString()}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ bgcolor: 'grey.800', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton 
            onClick={handleZoomOut} 
            disabled={zoom <= 0.25}
            sx={{ color: 'white' }}
            title="Zoom Out"
          >
            <ZoomOut />
          </IconButton>
          <Typography variant="body2" sx={{ 
            color: 'white', 
            display: 'flex', 
            alignItems: 'center',
            minWidth: '60px',
            justifyContent: 'center'
          }}>
            {Math.round(zoom * 100)}%
          </Typography>
          <IconButton 
            onClick={handleZoomIn} 
            disabled={zoom >= 3}
            sx={{ color: 'white' }}
            title="Zoom In"
          >
            <ZoomIn />
          </IconButton>
          
          <IconButton 
            onClick={handleRotateLeft}
            sx={{ color: 'white' }}
            title="Rotate Left"
          >
            <RotateLeft />
          </IconButton>
          <IconButton 
            onClick={handleRotateRight}
            sx={{ color: 'white' }}
            title="Rotate Right"
          >
            <RotateRight />
          </IconButton>
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            onClick={toggleFullscreen}
            startIcon={fullscreen ? <FullscreenExit /> : <Fullscreen />}
            sx={{ color: 'white' }}
          >
            {fullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
          </Button>
          <Button
            onClick={handleDownload}
            startIcon={<Download />}
            sx={{ color: 'white' }}
            disabled={!documentUrl}
          >
            Download
          </Button>
          <Button onClick={onClose} sx={{ color: 'white' }}>
            Close
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default DocumentViewerModal;